import { useState, useEffect } from 'react'
import {
  getUnassociatedTraffic,
  setTrafficReconciliation,
  UnassociatedCampaign,
  DistributionMethod,
} from '../services/reconciliation'
import LoadingSpinner from '../components/LoadingSpinner'

// Mismas 4 opciones de flow que ya usa FilterBar.tsx en el resto del
// dashboard (inferidas del texto de la URL en todas las demás vistas).
const FLOW_OPTIONS = [
  { value: 'seguro-salud', label: 'Seguro Salud' },
  { value: 'creditos-personales', label: 'Créditos Personales' },
  { value: 'tarjeta-credito', label: 'Tarjeta de Crédito' },
  { value: 'seguro-decesos', label: 'Seguro Decesos' },
]

interface RowProps {
  data: UnassociatedCampaign
  onSaved: () => void
}

/**
 * H5 (Fase C #7): fila editable — asigna un flow y guarda con criterio de
 * reparto "por gasto" (el único disponible de verdad, ver comentario más
 * abajo). Tras guardar, se recarga toda la lista para obtener el reparto
 * recalculado por el backend, en vez de intentar duplicar ese cálculo aquí.
 */
const ReconciliationRow = ({ data, onSaved }: RowProps) => {
  const [flow, setFlow] = useState(data.flow || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (!flow) {
      setError('Elige un flow antes de guardar')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await setTrafficReconciliation(data.campaign, flow, 'spend' as DistributionMethod)
      onSaved()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al guardar')
      console.error('Error guardando reconciliación:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <tr>
      <td className="max-w-xs truncate" title={data.campaign}>
        {data.campaign}
      </td>
      <td className="text-sm text-dashboard-muted">
        {data.dateRange.start} → {data.dateRange.end}
      </td>
      <td className="font-semibold">{data.leads.toLocaleString()}</td>
      <td className="font-semibold">€{data.revenue.toFixed(2)}</td>
      <td>
        <select
          value={flow}
          onChange={(e) => setFlow(e.target.value)}
          aria-label={`Flow de ${data.campaign}`}
          className="filter-select text-sm w-44"
        >
          <option value="">Sin asignar</option>
          {FLOW_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </td>
      <td>
        <div className="text-xs space-y-1">
          <label className="flex items-center gap-1.5">
            <input type="radio" checked readOnly />
            Por gasto
          </label>
          <label
            className="flex items-center gap-1.5 text-dashboard-muted cursor-not-allowed"
            title="No disponible: ninguna plataforma de medios (Google/Facebook/Microsoft/TikTok) importa datos de clics todavía, solo gasto."
          >
            <input type="radio" disabled />
            Por clics (no disponible)
          </label>
        </div>
      </td>
      <td>
        {data.split ? (
          <div className="text-xs space-y-0.5">
            <div>Google: {data.split.google.sharePct.toFixed(0)}%</div>
            <div>Facebook: {data.split.facebook.sharePct.toFixed(0)}%</div>
            <div>Microsoft: {data.split.microsoft.sharePct.toFixed(0)}%</div>
            <div>TikTok: {data.split.tiktok.sharePct.toFixed(0)}%</div>
          </div>
        ) : data.flow ? (
          <span className="text-xs text-dashboard-muted">Sin gasto en ese periodo</span>
        ) : (
          <span className="text-xs text-dashboard-muted">—</span>
        )}
      </td>
      <td>
        <button onClick={handleSave} disabled={saving} className="btn-secondary text-sm disabled:opacity-50">
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
        {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
      </td>
    </tr>
  )
}

/**
 * H5 del roadmap (Fase C #7): "clics/conversiones que llegan sin
 * ext_source_id o source_page" — en los datos reales, source_page nunca
 * falta; el problema real es ext_source_id ausente (25 de 476 registros,
 * ~30 leads / 15€). Ya se cuentan bien en todos los totales del dashboard
 * (la atribución a canal es por nombre de campaña, no por ext_source_id),
 * pero no hay forma de saber a qué medio de tráfico corresponden — de ahí
 * la reconciliación manual.
 */
const UnassociatedTraffic = () => {
  const [campaigns, setCampaigns] = useState<UnassociatedCampaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      const res = await getUnassociatedTraffic()
      setCampaigns(res.data.campaigns)
      setError(null)
    } catch (err) {
      setError('Error al cargar el tráfico sin asociar')
      console.error('Error fetching unassociated traffic:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (loading) {
    return <LoadingSpinner />
  }

  const totalLeads = campaigns.reduce((sum, c) => sum + c.leads, 0)
  const totalRevenue = campaigns.reduce((sum, c) => sum + c.revenue, 0)

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
          <span className="mr-3">🔀</span>
          TRÁFICO SIN ASOCIAR
        </h1>
        <p className="text-dashboard-muted mt-1">
          Leads de Leadcentre sin identificador de medio (ext_source_id) — ya se cuentan en
          los totales del dashboard, pero no sabemos a qué canal atribuirlos. Asigna un flow y
          repártelos entre canales según su peso de gasto en ese periodo.
        </p>
      </div>

      {error ? (
        <div className="card p-6 text-center text-dashboard-muted">{error}</div>
      ) : campaigns.length === 0 ? (
        <div className="card p-6 text-center text-dashboard-muted">
          No hay tráfico sin asociar — todos los registros tienen ext_source_id. 🎉
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="metric-card">
              <p className="metric-label">Campañas sin asociar</p>
              <p className="metric-value">{campaigns.length}</p>
            </div>
            <div className="metric-card">
              <p className="metric-label">Leads sin asociar</p>
              <p className="metric-value">{totalLeads.toLocaleString()}</p>
            </div>
            <div className="metric-card">
              <p className="metric-label">Revenue sin asociar</p>
              <p className="metric-value">€{totalRevenue.toFixed(2)}</p>
            </div>
          </div>

          <div className="card">
            <div className="p-6 border-b border-dashboard-border">
              <h2 className="text-xl font-bold text-dashboard-text">Campañas sin ext_source_id</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Campaña</th>
                    <th>Periodo</th>
                    <th>Leads</th>
                    <th>Revenue</th>
                    <th>Flow</th>
                    <th>Criterio de reparto</th>
                    <th>Reparto entre canales</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <ReconciliationRow key={c.campaign} data={c} onSaved={fetchData} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default UnassociatedTraffic
